import { useState } from 'react';
import { Input, InputNumber, DatePicker, Select, Button, Space, Tooltip, message } from 'antd';
import { EditOutlined, CheckOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import GeoLocationInput from './GeoLocationInput';
import './InlineEditField.css';

/**
 * A single value that edits in place: double-click it (or the pencil that
 * appears on hover) to swap the display for an editor, Enter/✓ saves, Esc/✕
 * cancels. Saving is explicit rather than on blur because the date and geo
 * editors open popups that steal focus.
 *
 *   <InlineEditField
 *       canEdit={showEdit}
 *       type="text"                // text | textarea | number | date | geo | geo_multi | select
 *       options={['A', 'B']}       // select only: the allowed values
 *       value={entity.url}         // raw stored value
 *       display={<a href=…>…</a>}  // what to show when not editing
 *       onSave={v => api.put(…)}   // returns a promise; editor closes on resolve
 *   />
 *
 * `onSave` receives the normalised value: trimmed string (null if empty),
 * number, ISO date string, { label, lat, lng } for geo, or an array of
 * those for geo_multi. `validate(value)`
 * may return an error string to block the save (the editor stays open).
 *
 * `emptyPrompt` (e.g. "Add a description"): while the value is blank, show a
 * one-click "+ prompt" link in place of `display`, to nudge filling it in.
 */
function InlineEditField({ value, display, type = 'text', options, canEdit, onSave, validate, required = false, placeholder, emptyPrompt }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);

    if (!canEdit) return display;

    const startEditing = () => {
        setDraft(type === 'date' ? (value ? dayjs(value) : null) : (value ?? null));
        setEditing(true);
    };

    const cancel = () => { setEditing(false); setDraft(null); };

    const normalised = () => {
        if (type === 'date') return draft ? draft.toISOString() : null;
        if (type === 'number' || type === 'select') return draft ?? null;
        if (type === 'geo') return draft && draft.lat != null && draft.lng != null ? draft : null;
        if (type === 'geo_multi') {
            const places = (Array.isArray(draft) ? draft : []).filter(p => p && p.lat != null && p.lng != null);
            return places.length ? places : null;
        }
        const s = (draft ?? '').trim();
        return s || null;
    };

    const save = async () => {
        const next = normalised();
        if (required && next === null) return;
        const error = next !== null && validate?.(next);
        if (error) { message.error(error); return; }
        setSaving(true);
        try {
            await onSave(next);
            setEditing(false);
        } catch {
            // Caller reports the error; stay in edit mode so the draft isn't lost.
        } finally {
            setSaving(false);
        }
    };

    const onKeyDown = e => {
        if (e.key === 'Escape') { e.preventDefault(); cancel(); }
        // Plain Enter saves single-line editors; textarea needs Cmd/Ctrl+Enter
        // so Enter can still insert newlines.
        // Select uses Enter to pick the highlighted option, so it saves via ✓ only.
        if (e.key === 'Enter' && type !== 'select' && (type !== 'textarea' || e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            save();
        }
    };

    if (editing) {
        const common = { autoFocus: true, disabled: saving, placeholder, onKeyDown };
        const editor = type === 'textarea' ? (
            <Input.TextArea {...common} autoSize={{ minRows: 2, maxRows: 10 }} value={draft ?? ''} onChange={e => setDraft(e.target.value)} />
        ) : type === 'number' ? (
            <InputNumber {...common} style={{ width: '100%' }} value={draft} onChange={setDraft} />
        ) : type === 'select' ? (
            <Select
                {...common}
                allowClear
                defaultOpen
                style={{ width: '100%' }}
                value={draft ?? undefined}
                onChange={v => setDraft(v ?? null)}
                // Keep a saved value whose option was since removed selectable.
                options={[...new Set([...(options || []), ...(draft ? [draft] : [])])].map(o => ({ value: o, label: o }))}
            />
        ) : type === 'date' ? (
            <DatePicker {...common} style={{ width: '100%' }} value={draft} onChange={setDraft} />
        ) : type === 'geo' || type === 'geo_multi' ? (
            <div onKeyDown={e => { if (e.key === 'Escape') cancel(); }}>
                <GeoLocationInput multiple={type === 'geo_multi'} value={draft || undefined} onChange={setDraft} />
            </div>
        ) : (
            <Input {...common} value={draft ?? ''} onChange={e => setDraft(e.target.value)} />
        );
        return (
            <div className="inline-edit inline-edit--editing">
                <div className="inline-edit-editor">{editor}</div>
                <Space size={2}>
                    <Tooltip title={type === 'textarea' ? 'Save (⌘/Ctrl+Enter)' : 'Save (Enter)'}>
                        <Button size="small" type="primary" icon={<CheckOutlined />} loading={saving} onClick={save}
                            disabled={required && normalised() === null} aria-label="Save" />
                    </Tooltip>
                    <Tooltip title="Cancel (Esc)">
                        <Button size="small" icon={<CloseOutlined />} disabled={saving} onClick={cancel} aria-label="Cancel" />
                    </Tooltip>
                </Space>
            </div>
        );
    }

    if (emptyPrompt && isBlank(value)) {
        return (
            <Button type="link" size="small" className="inline-edit-add" icon={<PlusOutlined />} onClick={startEditing}>
                {emptyPrompt}
            </Button>
        );
    }

    return (
        <div
            className="inline-edit"
            onDoubleClick={() => {
                window.getSelection()?.removeAllRanges(); // double-click selects a word; drop it
                startEditing();
            }}
            title="Double-click to edit"
        >
            <div className="inline-edit-display">{display}</div>
            <Button
                type="text"
                size="small"
                className="inline-edit-pencil"
                icon={<EditOutlined />}
                onClick={startEditing}
                aria-label="Edit"
            />
        </div>
    );
}

// Blank as far as "is this filled in?" goes: 0 and false count as answers.
export function isBlank(value) {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) return value.length === 0;
    return false;
}

export default InlineEditField;
